import { usePharmacistShipment } from '@/context/shipments/PharmacistShipment';
import theme from '@montugroup/circuit-shared/theme';
import type { ConsignmentItemResponseSchema } from '@montugroup/contracts-warehousing-api';
import { Box, Stack, Table } from '@montugroup/design-system';
import { debounce, Grid2, useMediaQuery } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ConsignmentAcknowledgementForm from '../ConsignmentAcknowledmentForm';
import { QuantityReceivedTableCell } from './cells/PharmacistShipmentsTableCell';

const MIN_TABLE_HEIGHT = 400;

function ConsignmentAcknowledgementItemsTable() {
  const { shipment, loading, receivedQuantities, setReceivedQuantities, submitted, disableForm } =
    usePharmacistShipment();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(MIN_TABLE_HEIGHT);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight;

        const FOOTER_HEIGHT = 95; // px
        const availableHeight = viewportHeight - containerTop - FOOTER_HEIGHT;

        setContainerHeight(Math.max(availableHeight, MIN_TABLE_HEIGHT));
      }
    };

    handleResize();
    const debounced = debounce(handleResize, 100);
    window.addEventListener('resize', debounced);
    return () => window.removeEventListener('resize', debounced);
  }, []);

  const columns: ColumnDef<ConsignmentItemResponseSchema>[] = useMemo(() => {
    if (isMobile) {
      return [
        {
          accessorKey: 'productName',
          header: 'Product Name',
          cell: ({ row }) => <Box>{row.original.productName}</Box>,
        },
        {
          accessorKey: 'quantity',
          header: 'Quantities',
          cell: ({ row }) => (
            <Stack direction={'column'} alignItems={'center'} gap={2}>
              <Box>{row.original.quantity}</Box>
              <QuantityReceivedTableCell
                disabled={row.original.quantityReceived !== null || disableForm}
                max={row.original.quantity}
                value={receivedQuantities[row.original.id] ?? row.original.quantityReceived ?? row.original.quantity}
                onChange={(value) =>
                  setReceivedQuantities((prev) => ({
                    ...prev,
                    [row.original.id]: value,
                  }))
                }
              />
            </Stack>
          ),
        },
      ];
    }

    return [
      {
        accessorKey: 'productName',
        header: 'Product Name',
        enableSorting: true,
        cell: ({ row }) => <Box>{row.original.productName}</Box>,
      },
      {
        accessorKey: 'quantity',
        header: 'Ordered Quantity',
        cell: ({ row }) => <Box>{row.original.quantity}</Box>,
        enableSorting: false,
      },
      {
        header: 'Quantity Received',
        enableSorting: false,
        cell: ({ row }) => (
          <QuantityReceivedTableCell
            disabled={row.original.quantityReceived !== null || disableForm}
            max={row.original.quantity}
            value={receivedQuantities[row.original.id] ?? row.original.quantityReceived ?? row.original.quantity}
            onChange={(value) => {
              setReceivedQuantities((prev) => ({
                ...prev,
                [row.original.id]: value,
              }));
            }}
          />
        ),
      },
    ];
  }, [isMobile, submitted, shipment, disableForm, shipment?.items]);

  return (
    <Grid2 container spacing={4}>
      <Grid2 size={{ sm: 12, md: 8 }}>
        <Box ref={containerRef}>
          <Table
            data={shipment?.items ?? []}
            columns={columns}
            isLoading={!shipment && loading}
            isScrollable
            maxHeight={containerHeight}
            showPagination={false}
            pageSize={shipment?.items.length ?? 0}
          />
        </Box>
      </Grid2>
      <Grid2 size={{ sm: 12, md: 4 }}>
        <ConsignmentAcknowledgementForm />
      </Grid2>
    </Grid2>
  );
}

export default ConsignmentAcknowledgementItemsTable;
